import { useState } from 'react';
import { useAnimationStore } from '../stores/animationStore';
import { ExportedPreset } from '../types';
import { generateExportCode, getFileExtension, getMimeType } from '../utils/exportTemplates';

type ExportFormat = 'js' | 'json' | 'html';

export const ExportPanel = () => {
  const { currentPreset } = useAnimationStore();
  const [exportFormat, setExportFormat] = useState<ExportFormat>('js');

  if (!currentPreset) return null;

  const handleExport = () => {
    const exported: ExportedPreset = {
      version: '1.0.0',
      id: currentPreset.id,
      name: currentPreset.name,
      category: currentPreset.category,
      subType: currentPreset.subType,
      config: currentPreset.config,
      engine: '@animationconstructor/engine@1.0.0',
      createdAt: new Date().toISOString()
    };

    const code = generateExportCode(exported, exportFormat);
    const ext = getFileExtension(exportFormat);
    const mime = getMimeType(exportFormat);
    
    const blob = new Blob([code], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentPreset.name}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getFormatDescription = () => {
    switch (exportFormat) {
      case 'json':
        return 'JSON - Конфиг для API и сохранения';
      case 'html':
        return 'HTML - Готовый файл для демо';
      case 'js':
        return `JS - ${currentPreset.category === 'buttons' ? 'Canvas overlay для элемента' : 'Canvas анимация'}`;
    }
  };

  const getCategoryInfo = () => {
    const categoryInfo: Record<string, string> = {
      backgrounds: 'Canvas с частицами',
      buttons: 'Canvas overlay с огоньком',
      text: 'CSS анимация текста',
      loaders: 'CSS/Canvas лоадер',
      cards: 'CSS анимация карточек'
    };
    return categoryInfo[currentPreset.category] || 'Unknown';
  };

  return (
    <div style={{
      background: '#1a1a1a',
      borderRadius: 12,
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 16
    }}>
      <h3 style={{ color: '#fff', margin: 0 }}>Экспорт</h3>

      {/* Category info */}
      <div style={{
        background: '#252525',
        borderRadius: 6,
        padding: 10,
        fontSize: 12,
        color: '#888'
      }}>
        <div style={{ color: '#fff', marginBottom: 4 }}>{currentPreset.category} → {currentPreset.subType}</div>
        <div>{getCategoryInfo()}</div>
      </div>

      {/* Format selector */}
      <div style={{ display: 'flex', gap: 8 }}>
        {(['js', 'html', 'json'] as const).map(fmt => (
          <button
            key={fmt}
            onClick={() => setExportFormat(fmt)}
            style={{
              background: exportFormat === fmt ? '#3498db' : '#2a2a2a',
              border: '1px solid #333',
              borderRadius: 6,
              padding: '8px 16px',
              color: '#fff',
              fontSize: 13,
              cursor: 'pointer',
              textTransform: 'uppercase'
            }}
          >
            {fmt}
          </button>
        ))}
      </div>

      {/* Export button */}
      <button
        onClick={handleExport}
        style={{
          background: '#27ae60',
          border: 'none',
          borderRadius: 6,
          padding: '12px 16px',
          color: '#fff',
          fontSize: 14,
          cursor: 'pointer',
          fontWeight: 600
        }}
      >
        Скачать файл
      </button>

      {/* Description */}
      <div style={{
        background: '#0a0a0a',
        borderRadius: 8,
        padding: 12,
        fontSize: 12,
        color: '#888',
        fontFamily: 'monospace'
      }}>
        {getFormatDescription()}
      </div>

      {/* Usage hint for buttons */}
      {currentPreset.category === 'buttons' && (
        <div style={{
          background: '#1a3a1a',
          borderRadius: 6,
          padding: 10,
          fontSize: 11,
          color: '#8f8'
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Использование:</div>
          <code style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
{`<button data-animate="${currentPreset.name.replace(/\s/g, '')}">
  Нажми меня
</button>

<script src="${currentPreset.name.replace(/\s/g, '')}.js"></script>`}
          </code>
        </div>
      )}
    </div>
  );
};